import React, { useState, useEffect } from 'react';
import {
  FaPlus as Plus,
  FaEdit as Edit,
  FaTrash as Delete,
  FaSearch as Search,
  FaFilter as Filter,
  FaCopy as Copy,
  FaEye as View,
  FaDownload as Download,
  FaCalendarAlt as Calendar,
  FaUsers as Users,
  FaPercentage as Percentage,
  FaDollarSign as Dollar,
  FaTags as Tags,
  FaChartLine as Chart,
  FaCheckCircle as CheckCircle,
  FaTimesCircle as TimesCircle,
  FaClock as Clock,
  FaExclamationTriangle as Warning,
} from 'react-icons/fa';
import {
  Coupon,
  CouponFilters,
  CouponStatus,
  CouponType,
  CouponCategory,
  CreateCouponForm,
  CouponDashboard,
  CouponPerformance,
} from '../../types/coupons';
import { useAuth } from '../../hooks/useAuth';

interface CouponsManagementProps {}

const CouponsManagement: React.FC<CouponsManagementProps> = () => {
  const { user } = useAuth();
  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [filteredCoupons, setFilteredCoupons] = useState<Coupon[]>([]);
  const [filters, setFilters] = useState<CouponFilters>({});
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedCoupon, setSelectedCoupon] = useState<Coupon | null>(null);
  const [dashboard, setDashboard] = useState<CouponDashboard | null>(null);
  const [activeTab, setActiveTab] = useState<
    'overview' | 'coupons' | 'analytics'
  >('overview');

  // بيانات تجريبية للكوبونات
  useEffect(() => {
    const mockCoupons: Coupon[] = [
      {
        id: '1',
        code: 'WELCOME2024',
        title: 'خصم ترحيبي للطلاب الجدد',
        description: 'خصم 20% للطلاب المسجلين حديثاً في المنصة',
        type: 'percentage',
        value: 20,
        category: 'welcome',
        status: 'active',
        usageLimit: 100,
        usageCount: 45,
        validFrom: '2024-01-01T00:00:00Z',
        validUntil: '2024-12-31T23:59:59Z',
        createdAt: new Date().toISOString(),
        createdBy: user?.id || 'admin',
        updatedAt: new Date().toISOString(),
        conditions: {
          minAmount: 100,
          applicableRoles: ['student'],
          applicableServices: ['tuition', 'materials'],
          firstTimeOnly: true,
          userUsageLimit: 1,
        },
        metadata: {
          campaign: 'new_student_campaign',
          source: 'website',
          tags: ['new_users', 'students'],
          priority: 1,
          stackable: false,
          autoApply: true,
          analytics: {
            impressions: 500,
            clicks: 120,
            uses: 45,
            conversions: 45,
            revenue: 4500,
            savings: 1125,
          },
        },
      },
      {
        id: '2',
        code: 'PARENT50',
        title: 'خصم لأولياء الأمور',
        description: 'خصم 50 جنيهًا عند التسجيل لأكثر من طالب',
        type: 'fixed_amount',
        value: 50,
        category: 'loyalty',
        status: 'active',
        usageLimit: 200,
        usageCount: 78,
        validFrom: '2024-01-01T00:00:00Z',
        validUntil: '2024-06-30T23:59:59Z',
        createdAt: new Date(Date.now() - 2592000000).toISOString(), // منذ 30 يوم
        createdBy: user?.id || 'admin',
        updatedAt: new Date(Date.now() - 86400000).toISOString(), // منذ يوم
        conditions: {
          minAmount: 200,
          applicableRoles: ['parent'],
          applicableServices: ['tuition'],
          userUsageLimit: 1,
        },
        metadata: {
          campaign: 'parent_loyalty',
          source: 'email',
          tags: ['parents', 'multi_student'],
          priority: 2,
          stackable: true,
          analytics: {
            impressions: 300,
            clicks: 95,
            uses: 78,
            conversions: 78,
            revenue: 7800,
            savings: 3900,
          },
        },
      },
      {
        id: '3',
        code: 'RAMADAN2024',
        title: 'خصم رمضان الكريم',
        description: 'خصم 15% بمناسبة شهر رمضان المبارك',
        type: 'percentage',
        value: 15,
        category: 'seasonal',
        status: 'expired',
        usageLimit: 500,
        usageCount: 234,
        validFrom: '2024-03-01T00:00:00Z',
        validUntil: '2024-04-15T23:59:59Z',
        createdAt: new Date(Date.now() - 7776000000).toISOString(), // منذ 90 يوم
        createdBy: user?.id || 'admin',
        updatedAt: new Date(Date.now() - 7776000000).toISOString(),
        conditions: {
          applicableRoles: ['student', 'parent'],
          applicableServices: ['tuition', 'materials', 'exam_prep'],
          userUsageLimit: 2,
        },
        metadata: {
          campaign: 'ramadan_2024',
          source: 'social_media',
          tags: ['seasonal', 'ramadan', 'limited_time'],
          priority: 1,
          stackable: false,
          analytics: {
            impressions: 1200,
            clicks: 450,
            uses: 234,
            conversions: 234,
            revenue: 15600,
            savings: 2730,
          },
        },
      },
    ];

    setCoupons(mockCoupons);
    setFilteredCoupons(mockCoupons);

    // بيانات تجريبية للوحة التحكم
    const mockDashboard: CouponDashboard = {
      totalCoupons: mockCoupons.length,
      activeCoupons: mockCoupons.filter(c => c.status === 'active').length,
      totalUsage: mockCoupons.reduce((sum, c) => sum + c.usageCount, 0),
      totalSavings: mockCoupons.reduce(
        (sum, c) => sum + (c.metadata.analytics?.savings || 0),
        0
      ),
      conversionRate: 68.5,
      topPerformingCoupons: [
        {
          coupon: mockCoupons[0],
          usageCount: 45,
          conversionRate: 37.5,
          totalSavings: 1125,
          revenue: 4500,
        },
        {
          coupon: mockCoupons[1],
          usageCount: 78,
          conversionRate: 82.1,
          totalSavings: 3900,
          revenue: 7800,
        },
      ],
      usageTrends: [
        { date: '2024-01-01', usage: 12, savings: 300, newUsers: 8 },
        { date: '2024-01-02', usage: 18, savings: 450, newUsers: 12 },
        { date: '2024-01-03', usage: 24, savings: 600, newUsers: 15 },
      ],
      categorySummary: [
        { category: 'welcome', count: 1, usage: 45, savings: 1125 },
        { category: 'loyalty', count: 1, usage: 78, savings: 3900 },
        { category: 'seasonal', count: 1, usage: 234, savings: 2730 },
      ],
    };

    setDashboard(mockDashboard);
    setIsLoading(false);
  }, [user?.id]);

  // تطبيق الفلاتر والبحث
  useEffect(() => {
    let filtered = [...coupons];

    // البحث
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        coupon =>
          coupon.code.toLowerCase().includes(query) ||
          coupon.title.toLowerCase().includes(query) ||
          coupon.description.toLowerCase().includes(query)
      );
    }

    // فلترة حسب الحالة
    if (filters.status && filters.status.length > 0) {
      filtered = filtered.filter(coupon =>
        filters.status!.includes(coupon.status)
      );
    }

    // فلترة حسب الفئة
    if (filters.category && filters.category.length > 0) {
      filtered = filtered.filter(coupon =>
        filters.category!.includes(coupon.category)
      );
    }

    // فلترة حسب النوع
    if (filters.type && filters.type.length > 0) {
      filtered = filtered.filter(coupon => filters.type!.includes(coupon.type));
    }

    setFilteredCoupons(filtered);
  }, [coupons, searchQuery, filters]);

  // الحصول على لون الحالة
  const getStatusColor = (status: CouponStatus) => {
    switch (status) {
      case 'active':
        return 'text-green-600 bg-green-100 dark:bg-green-900 dark:text-green-300';
      case 'inactive':
        return 'text-gray-600 bg-gray-100 dark:bg-gray-700 dark:text-gray-300';
      case 'expired':
        return 'text-red-600 bg-red-100 dark:bg-red-900 dark:text-red-300';
      case 'exhausted':
        return 'text-orange-600 bg-orange-100 dark:bg-orange-900 dark:text-orange-300';
      case 'scheduled':
        return 'text-blue-600 bg-blue-100 dark:bg-blue-900 dark:text-blue-300';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  // الحصول على أيقونة النوع
  const getTypeIcon = (type: CouponType) => {
    switch (type) {
      case 'percentage':
        return <span className='w-4 h-4 text-center text-xs font-bold'>%</span>;
      case 'fixed_amount':
        return <span className='w-4 h-4 text-center text-xs font-bold'>$</span>;
      default:
        return <span className='w-4 h-4 text-center text-xs font-bold'>#</span>;
    }
  };

  // نسخ كود الكوبون
  const copyCode = (code: string) => {
    navigator.clipboard.writeText(code);
    // يمكن إضافة toast notification هنا
  };

  // حذف كوبون
  const handleDeleteCoupon = (coupon: Coupon) => {
    setSelectedCoupon(coupon);
    setShowDeleteModal(true);
  };

  // تأكيد الحذف
  const confirmDelete = () => {
    if (selectedCoupon) {
      setCoupons(prev => prev.filter(c => c.id !== selectedCoupon.id));
      setShowDeleteModal(false);
      setSelectedCoupon(null);
    }
  };

  // تنسيق التاريخ
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('ar-SA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // تنسيق العملة
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('ar-SA', {
      style: 'currency',
      currency: 'EGP',
    }).format(amount);
  };

  // عرض لوحة التحكم
  const renderOverviewTab = () => (
    <div className='space-y-6'>
      {/* إحصائيات سريعة */}
      <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6'>
        <div className='bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700'>
          <div className='flex items-center justify-between'>
            <div>
              <p className='text-sm font-medium text-gray-600 dark:text-gray-400'>
                إجمالي الكوبونات
              </p>
              <p className='text-2xl font-bold text-gray-900 dark:text-white'>
                {dashboard?.totalCoupons}
              </p>
            </div>
            <span className='w-8 h-8 text-4xl text-blue-600'>#</span>
          </div>
        </div>

        <div className='bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700'>
          <div className='flex items-center justify-between'>
            <div>
              <p className='text-sm font-medium text-gray-600 dark:text-gray-400'>
                كوبونات نشطة
              </p>
              <p className='text-2xl font-bold text-green-600'>
                {dashboard?.activeCoupons}
              </p>
            </div>
            <span className='w-8 h-8 text-2xl text-green-600'>✓</span>
          </div>
        </div>

        <div className='bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700'>
          <div className='flex items-center justify-between'>
            <div>
              <p className='text-sm font-medium text-gray-600 dark:text-gray-400'>
                إجمالي الاستخدام
              </p>
              <p className='text-2xl font-bold text-blue-600'>
                {dashboard?.totalUsage}
              </p>
            </div>
            <span className='w-8 h-8 text-2xl text-blue-600'>👥</span>
          </div>
        </div>

        <div className='bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700'>
          <div className='flex items-center justify-between'>
            <div>
              <p className='text-sm font-medium text-gray-600 dark:text-gray-400'>
                إجمالي التوفير
              </p>
              <p className='text-2xl font-bold text-green-600'>
                {formatCurrency(dashboard?.totalSavings || 0)}
              </p>
            </div>
            <span className='w-8 h-8 text-2xl text-green-600'>$</span>
          </div>
        </div>
      </div>

      {/* أفضل الكوبونات أداءً */}
      <div className='bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700'>
        <h3 className='text-lg font-semibold text-gray-900 dark:text-white mb-4'>
          أفضل الكوبونات أداءً
        </h3>
        <div className='space-y-4'>
          {dashboard?.topPerformingCoupons.map((performance, index) => (
            <div
              key={performance.coupon.id}
              className='flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg'
            >
              <div className='flex items-center gap-4'>
                <div className='w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold'>
                  {index + 1}
                </div>
                <div>
                  <h4 className='font-medium text-gray-900 dark:text-white'>
                    {performance.coupon.title}
                  </h4>
                  <p className='text-sm text-gray-600 dark:text-gray-400'>
                    الكود: {performance.coupon.code}
                  </p>
                </div>
              </div>
              <div className='text-left'>
                <p className='text-sm text-gray-600 dark:text-gray-400'>
                  الاستخدام: {performance.usageCount}
                </p>
                <p className='text-sm text-gray-600 dark:text-gray-400'>
                  التوفير: {formatCurrency(performance.totalSavings)}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <div className='min-h-screen bg-gray-50 dark:bg-gray-900 p-6'>
      <div className='max-w-7xl mx-auto'>
        {/* رأس الصفحة */}
        <div className='mb-6'>
          <h1 className='text-3xl font-bold text-gray-900 dark:text-white'>
            إدارة الكوبونات
          </h1>
          <p className='text-gray-600 dark:text-gray-400 mt-2'>
            إدارة كوبونات الخصم وعروض الطلاب وأولياء الأمور
          </p>
        </div>

        {/* علامات التبويب */}
        <div className='flex space-x-8 border-b border-gray-200 dark:border-gray-700 mb-6'>
          <button
            onClick={() => setActiveTab('overview')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'overview'
                ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
          >
            نظرة عامة
          </button>
          <button
            onClick={() => setActiveTab('coupons')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'coupons'
                ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
          >
            الكوبونات
          </button>
          <button
            onClick={() => setActiveTab('analytics')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'analytics'
                ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
          >
            التحليلات
          </button>
        </div>

        {/* محتوى علامات التبويب */}
        {activeTab === 'overview' && renderOverviewTab()}

        {activeTab === 'coupons' && (
          <div className='space-y-6'>
            {/* شريط الأدوات */}
            <div className='bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700'>
              <div className='flex flex-col md:flex-row gap-4 items-center justify-between'>
                {/* البحث */}
                <div className='flex-1 max-w-md'>
                  <div className='relative'>
                    <span className='w-5 h-5 absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400'>
                      🔍
                    </span>
                    <input
                      type='text'
                      placeholder='البحث في الكوبونات...'
                      value={searchQuery}
                      onChange={e => setSearchQuery(e.target.value)}
                      className='w-full pr-10 pl-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white'
                    />
                  </div>
                </div>

                {/* الفلاتر */}
                <div className='flex items-center gap-3'>
                  <select
                    value={filters.status?.[0] || ''}
                    onChange={e =>
                      setFilters({
                        ...filters,
                        status: e.target.value
                          ? [e.target.value as CouponStatus]
                          : undefined,
                      })
                    }
                    className='px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white'
                    aria-label='فلترة حسب الحالة'
                  >
                    <option value=''>جميع الحالات</option>
                    <option value='active'>نشط</option>
                    <option value='inactive'>غير نشط</option>
                    <option value='expired'>منتهي الصلاحية</option>
                    <option value='exhausted'>مستنفد</option>
                  </select>

                  <button
                    onClick={() => setShowCreateModal(true)}
                    className='flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors'
                  >
                    <span className='w-4 h-4 text-center font-bold'>+</span>
                    إنشاء كوبون
                  </button>
                </div>
              </div>
            </div>

            {/* جدول الكوبونات */}
            <div className='bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden'>
              <div className='overflow-x-auto'>
                <table className='w-full divide-y divide-gray-200 dark:divide-gray-700'>
                  <thead className='bg-gray-50 dark:bg-gray-700'>
                    <tr>
                      <th className='px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider'>
                        الكوبون
                      </th>
                      <th className='px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider'>
                        النوع والقيمة
                      </th>
                      <th className='px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider'>
                        الاستخدام
                      </th>
                      <th className='px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider'>
                        الصلاحية
                      </th>
                      <th className='px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider'>
                        الحالة
                      </th>
                      <th className='px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider'>
                        الإجراءات
                      </th>
                    </tr>
                  </thead>
                  <tbody className='bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700'>
                    {isLoading ? (
                      <tr>
                        <td colSpan={6} className='px-6 py-12 text-center'>
                          <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto'></div>
                        </td>
                      </tr>
                    ) : filteredCoupons.length === 0 ? (
                      <tr>
                        <td
                          colSpan={6}
                          className='px-6 py-12 text-center text-gray-500 dark:text-gray-400'
                        >
                          لا توجد كوبونات تطابق البحث
                        </td>
                      </tr>
                    ) : (
                      filteredCoupons.map(coupon => (
                        <tr
                          key={coupon.id}
                          className='hover:bg-gray-50 dark:hover:bg-gray-700'
                        >
                          <td className='px-6 py-4'>
                            <div className='flex items-center gap-3'>
                              <div>
                                <div className='font-medium text-gray-900 dark:text-white'>
                                  {coupon.title}
                                </div>
                                <div className='flex items-center gap-2'>
                                  <code className='text-sm bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded'>
                                    {coupon.code}
                                  </code>
                                  <button
                                    onClick={() => copyCode(coupon.code)}
                                    className='text-gray-400 hover:text-blue-600'
                                    aria-label='نسخ الكود'
                                  >
                                    <span className='w-4 h-4' >📋</span>
                                  </button>
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className='px-6 py-4'>
                            <div className='flex items-center gap-2'>
                              {getTypeIcon(coupon.type)}
                              <span className='text-sm text-gray-900 dark:text-white'>
                                {coupon.type === 'percentage'
                                  ? `${coupon.value}%`
                                  : formatCurrency(coupon.value)}
                              </span>
                            </div>
                          </td>
                          <td className='px-6 py-4'>
                            <div>
                              <div className='text-sm text-gray-900 dark:text-white'>
                                {coupon.usageCount} /{' '}
                                {coupon.usageLimit === -1
                                  ? '∞'
                                  : coupon.usageLimit}
                              </div>
                              <div className='w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 mt-1'>
                                <div
                                  className={`bg-blue-600 h-2 rounded-full transition-all duration-300 ${
                                    coupon.usageLimit === -1
                                      ? 'w-0'
                                      : coupon.usageCount >= coupon.usageLimit
                                      ? 'w-full'
                                      : coupon.usageCount / coupon.usageLimit >=
                                        0.75
                                      ? 'w-3/4'
                                      : coupon.usageCount / coupon.usageLimit >=
                                        0.5
                                      ? 'w-1/2'
                                      : coupon.usageCount / coupon.usageLimit >=
                                        0.25
                                      ? 'w-1/4'
                                      : 'w-1/12'
                                  }`}
                                />
                              </div>
                            </div>
                          </td>
                          <td className='px-6 py-4 text-sm text-gray-500 dark:text-gray-400'>
                            <div>من: {formatDate(coupon.validFrom)}</div>
                            <div>إلى: {formatDate(coupon.validUntil)}</div>
                          </td>
                          <td className='px-6 py-4'>
                            <span
                              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(
                                coupon.status
                              )}`}
                            >
                              {coupon.status === 'active' && 'نشط'}
                              {coupon.status === 'inactive' && 'غير نشط'}
                              {coupon.status === 'expired' && 'منتهي الصلاحية'}
                              {coupon.status === 'exhausted' && 'مستنفد'}
                              {coupon.status === 'scheduled' && 'مجدول'}
                            </span>
                          </td>
                          <td className='px-6 py-4'>
                            <div className='flex items-center gap-2'>
                              <button
                                onClick={() => {
                                  setSelectedCoupon(coupon);
                                  setShowEditModal(true);
                                }}
                                className='text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300'
                                aria-label='تعديل الكوبون'
                              >
                                <span className='w-4 h-4' >📋</span>
                              </button>
                              <button
                                onClick={() => handleDeleteCoupon(coupon)}
                                className='text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300'
                                aria-label='حذف الكوبون'
                              >
                                <span className='w-4 h-4' >📋</span>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'analytics' && (
          <div className='space-y-6'>
            <div className='bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700'>
              <h3 className='text-lg font-semibold text-gray-900 dark:text-white mb-4'>
                تحليلات متقدمة
              </h3>
              <p className='text-gray-600 dark:text-gray-400'>
                ستتوفر هذه الميزة قريباً مع تقارير مفصلة حول أداء الكوبونات
                ومعدلات التحويل.
              </p>
            </div>
          </div>
        )}

        {/* مودال تأكيد الحذف */}
        {showDeleteModal && selectedCoupon && (
          <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50'>
            <div className='bg-white dark:bg-gray-800 rounded-xl p-6 w-full max-w-md mx-4'>
              <div className='flex items-center gap-3 mb-4'>
                <span className='w-6 h-6 text-red-600' >📋</span>
                <h3 className='text-lg font-semibold text-gray-900 dark:text-white'>
                  تأكيد حذف الكوبون
                </h3>
              </div>
              <p className='text-gray-600 dark:text-gray-400 mb-6'>
                هل أنت متأكد من حذف الكوبون "{selectedCoupon.title}"؟ هذا
                الإجراء لا يمكن التراجع عنه.
              </p>
              <div className='flex justify-end gap-3'>
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className='px-4 py-2 text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200'
                >
                  إلغاء
                </button>
                <button
                  onClick={confirmDelete}
                  className='px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors'
                >
                  حذف
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CouponsManagement;

